import React from "react";
import { BsPatchCheck, BsLinkedin } from "react-icons/bs";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Clock from "../components/Clock";
import { Link, navigate } from "../router";
import { DOMESCRIBE_URL, LINKEDIN_URL } from "../config";

const Home: React.FC = () => {
  return (
    <main className="min-h-screen bg-[#F9FAFB] dark:bg-gray-900 text-domichron-text dark:text-white font-sans antialiased relative text-sm sm:text-base md:text-lg">
      <Header />

      {/* Hero Section */}
      <section className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 min-h-[60vh] py-12 px-6 lg:px-20 items-center bg-[#F9FAFB] dark:bg-gray-900">
        <div className="space-y-6 text-center lg:text-center w-full flex flex-col items-center justify-center">
          <h2 className="text-3xl sm:text-3xl md:text-5xl font-semibold leading-tight text-domichron-text dark:text-white">
            Building software for the long term.
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
            Domichron is a builder of high-leverage software ventures. Our
            current product is DomeScribe — AI transcription that turns any
            audio or video into accurate text.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center">
            <button
              onClick={() => navigate("/ventures/domescribe")}
              className="btn-primary"
            >
              Explore DomeScribe
            </button>
            <button
              type="button"
              onClick={() => navigate("/contact")}
              className="text-domichron-orange font-semibold hover:underline bg-transparent border-none cursor-pointer"
            >
              Get in touch
            </button>
          </div>
        </div>
        <Clock />
      </section>

      {/* Featured Venture: DomeScribe */}
      <section
        id="ventures"
        className="section-padding bg-white dark:bg-gray-800"
      >
        <motion.div
          whileHover={{ y: -2 }}
          className="relative rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white to-[#FFF6F0] dark:from-gray-900 dark:to-gray-900 shadow-lg max-w-6xl mx-auto"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-domichron-orange" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-8 md:p-12">
            <div className="md:col-span-2 space-y-5">
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-domichron-orange">
                Flagship venture
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-domichron-text dark:text-white flex flex-wrap items-center gap-2">
                DomeScribe
                <BsPatchCheck className="text-domichron-orange" />
              </h3>
              <p className="text-xl font-medium text-gray-800 dark:text-gray-200">
                Accurate AI transcription.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                Turn any audio or video into clean, accurate text — in 99
                languages. DomeScribe also handles the media work around it:
                extracting audio, converting formats, and compressing files.
              </p>
            </div>
            <div className="flex flex-col justify-between gap-6 md:border-l md:border-gray-200 md:dark:border-gray-700 md:pl-8">
              <div>
                <p className="text-xs font-semibold tracking-widest uppercase text-gray-500 dark:text-gray-400 mb-2">
                  Highlights
                </p>
                <ul className="space-y-1 text-base text-gray-800 dark:text-gray-200">
                  <li>Transcription in 99 languages</li>
                  <li>Private &amp; encrypted</li>
                  <li>Extract, convert &amp; compress</li>
                </ul>
              </div>
              <div className="flex flex-col gap-3">
                <a
                  href={DOMESCRIBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-center"
                >
                  Open DomeScribe
                </a>
                <Link
                  to="/ventures/domescribe"
                  className="text-center text-domichron-orange font-semibold hover:underline"
                >
                  Learn more
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Principles Section — unchanged */}
      <section
        id="principles"
        className="section-padding bg-[#F9FAFB] dark:bg-gray-900"
      >
        <h3 className="text-3xl font-bold mb-10 text-center text-domichron-text dark:text-white">
          Principles We Build By
        </h3>
        <div className="grid gap-10 md:grid-cols-4 max-w-7xl mx-auto">
          <motion.div whileHover={{ x: 4 }} className="relative">
            <div className="absolute -left-4 top-0 w-1 h-full bg-domichron-orange opacity-10" />
            <h4 className="font-semibold text-lg mb-1 text-domichron-text dark:text-white">
              Longevity
            </h4>
            <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              We prioritize foundational systems that endure beyond trends. Our
              work aims to remain relevant several decades from now.
            </p>
          </motion.div>
          <motion.div whileHover={{ x: 4 }} className="relative">
            <div className="absolute -left-4 top-0 w-1 h-full bg-domichron-orange opacity-10" />
            <h4 className="font-semibold text-lg mb-1 text-domichron-text dark:text-white">
              Integrity
            </h4>
            <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              We approach every decision with care, building in ways that align
              with our principles, honor our commitments, and reinforce
              long-term trust.
            </p>
          </motion.div>
          <motion.div whileHover={{ x: 4 }} className="relative">
            <div className="absolute -left-4 top-0 w-1 h-full bg-domichron-orange opacity-10" />
            <h4 className="font-semibold text-lg mb-1 text-domichron-text dark:text-white">
              Focus
            </h4>
            <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              We eliminate unnecessary complexity, prioritize what moves the
              mission forward, and give our undivided attention to one problem
              at a time.
            </p>
          </motion.div>
          <motion.div whileHover={{ x: 4 }} className="relative">
            <div className="absolute -left-4 top-0 w-1 h-full bg-domichron-orange opacity-10" />
            <h4 className="font-semibold text-lg mb-1 text-domichron-text dark:text-white">
              Vision + Execution
            </h4>
            <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              We approach every idea with a long-range lens, then execute with
              technical depth, structural discipline, and a relentless focus on
              delivery.
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className="section-padding bg-[#F1F5F9] dark:bg-gray-800 text-center"
      >
        <h3 className="text-3xl font-bold mb-6 text-domichron-text dark:text-white">
          About Domichron
        </h3>
        <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
          Domichron is a parent company building software ventures at the
          foundations of how the modern world works — the tools and systems
          people rely on every day. We focus on problems that compound over
          time — the kind where a carefully built product keeps mattering for
          decades. Founded in 2025.
        </p>
      </section>

      {/* Footer */}
      <footer className="py-8 mt-16 border-t border-gray-200 dark:border-gray-800">
        <div className="flex flex-col items-center gap-3 px-6">
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-base text-gray-700 dark:text-gray-300 hover:text-domichron-orange transition-colors"
          >
            <BsLinkedin className="w-5 h-5" />
            <span>Connect on LinkedIn</span>
          </a>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Domichron, Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
};

export default Home;
